import { Device, DeviceListRequest, getDeviceList } from '@/api/device';
import { getLightDeviceTypeTree, getSunLightDeviceTypeTree } from '@/api/deviceType';
import { formatOptionData, getChildNodesByParentIds } from '@/shared/components/CascadingMultipleSelector/utils';
import {
  CustomFilter,
  EllipsisSpan,
  Form,
  Input,
  Paging,
  Select,
  Table,
  useAsync,
  usePaging,
} from '@maxtropy/components';
import { apiV2ComprehensiveLabelListPost } from '@maxtropy/device-customer-apis-v2';
import { Cascader } from 'antd';
import { DefaultOptionType } from 'antd/es/cascader';
import React, { Key, Ref, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useRequest } from 'ahooks';
import styles from '../index.module.scss';
import { useQuery } from '@/utils/utils';

interface FilterParams {
  name?: string | undefined;
  tag?: string | undefined;
  typeId: Array<Array<number>> | undefined;
}

export interface DeviceModalRef {
  selectedRowKeys?: Key[];
}

type SearchParams = Omit<DeviceListRequest, 'page' | 'size' | 'gatewayId' | 'iotProtocol'>;

interface DeviceModalProps {
  bindSunLightDeviceIds?: Key[];
}

const columns = () => [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备S/N码',
    dataIndex: 'sn',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备标签',
    dataIndex: 'tags',
    ellipsis: { showTitle: true },
    render: (v?: string[]) => <EllipsisSpan value={v ? v.join(',') : '--'} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const DeviceSunLightModal = React.forwardRef(
  ({ bindSunLightDeviceIds }: DeviceModalProps, ref: Ref<DeviceModalRef>) => {
    const pagingInfo = usePaging();
    const [form] = Form.useForm();
    const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
    const [searchParams, setSearchParams] = useState<SearchParams>();
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>();

    const deviceTypeData = useAsync(getSunLightDeviceTypeTree);

    const { data: labelList } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 3 }));

    const { data, isLoading }: { data?: Device[]; isLoading?: boolean } = useQuery(
      useCallback(async () => {
        if (!deviceTypeData) return;
        const res = await getDeviceList({
          ...searchParams,
          typeId:
            searchParams?.typeId ??
            getChildNodesByParentIds(
              deviceTypeData?.tree.map(i => [i.id]),
              deviceTypeData?.tree
            ),
          page: pageOffset,
          size: pageSize,
        });
        if (res) setTotalCount(res.total);
        return res.list;
      }, [pageOffset, pageSize, searchParams, setTotalCount, deviceTypeData])
    );

    useEffect(() => {
      setSelectedRowKeys(bindSunLightDeviceIds ? [bindSunLightDeviceIds as unknown as Key] : []);
    }, [bindSunLightDeviceIds]);

    const onReset = () => {
      form.resetFields();
      form.submit();
      setPageOffset(1);
    };

    const onFinish = (val: FilterParams) => {
      const typeId = getChildNodesByParentIds(val.typeId, deviceTypeData?.tree);
      const params: SearchParams = {
        codeOrName: val.name,
        tag: val.tag,
        typeId,
      };
      setSearchParams(params);
      setPageOffset(1);
    };

    const rowSelection = {
      type: 'radio' as const,
      preserveSelectedRowKeys: true,
      selectedRowKeys: selectedRowKeys ?? [],
      onChange: (selectedRowKeys: Key[]) => {
        setSelectedRowKeys(selectedRowKeys);
      },
    };

    useImperativeHandle(ref, () => ({
      selectedRowKeys,
    }));

    const filter = (inputValue: string, path: DefaultOptionType[]) =>
      path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

    return (
      <div className={styles.deviceModal}>
        <CustomFilter<FilterParams> form={form} onFinish={val => onFinish(val as FilterParams)} onReset={onReset}>
          <Form.Item name="name" label="编号/名称">
            <Input placeholder={'请输入编号或名称查询'} style={{ width: 160 }} />
          </Form.Item>

          <Form.Item name="typeId" label="所属类目">
            <Cascader
              options={formatOptionData(deviceTypeData)}
              allowClear={false}
              fieldNames={{ children: 'child' }}
              multiple
              maxTagCount="responsive"
              showSearch={{ filter }}
              placeholder={'请选择所属类目'}
            />
          </Form.Item>

          <Form.Item name="tag" label="标签">
            <Select
              placeholder="请选择"
              options={labelList?.list?.map(item => ({ value: item.id, label: item.name }))}
              allowClear
              mode="multiple"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </CustomFilter>

        <Table
          scroll={{ y: 300 }}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          onRow={record => ({
            style: { cursor: 'pointer' },
            onClick: () => {
              if (selectedRowKeys?.[0] === record.id) {
                // 点击已选中的 -> 取消
                setSelectedRowKeys([]);
              } else {
                setSelectedRowKeys([record.id]);
              }
            },
          })}
          columns={columns()}
          dataSource={data}
          bottomLeftExtra={
            <div className={styles.tips}>
              <span>总计</span>
              <span className={styles.red}>{(selectedRowKeys ?? []).length}</span>
              <span>项</span>
            </div>
          }
        >
          <Paging pagingInfo={pagingInfo} />
        </Table>
      </div>
    );
  }
);

export default DeviceSunLightModal;
